import { type FC } from 'react'

import type { ProspectAuditInstagramDto, ProspectAuditTikTokDto } from '@api'
import { Image, Link, Text, View } from '@react-pdf/renderer'

import launchImage from '@/assets/images/launch.png'
import { extractUsername } from '@/utils/socials'

import { STATUS_COLORS } from '../../export-prospect-audit/constants'
import { type FormattedSocialAuditItem } from '../../export-prospect-audit/types'
import { styles as commonStyles } from '../styles'
import { PERFORMANCE_POINTS, SOCIAL_LABELS } from './constants'
import { styles } from './styles'

interface Props {
  data: FormattedSocialAuditItem<
    ProspectAuditTikTokDto | ProspectAuditInstagramDto
  >
}

export const SocialAuditBlock: FC<Props> = ({ data }) => {
  if (!data.accountUrl) {
    return null
  }

  const name = SOCIAL_LABELS[data.type]

  return (
    <View
      style={commonStyles.block}
      wrap={false}
    >
      <Text style={commonStyles.title}>{name} Audit</Text>
      <View style={commonStyles.header}>
        <View style={[commonStyles.card, commonStyles.flex]}>
          <Text style={commonStyles.headerTitle}>Account</Text>
          <View style={commonStyles.accountWrapper}>
            {data.avatarUrl && (
              <Image
                src={data.avatarUrl}
                style={commonStyles.avatar}
              />
            )}
            <View>
              <Text style={commonStyles.accountName}>
                @{extractUsername(data.accountUrl)}
              </Text>
              <View style={commonStyles.linkWrapper}>
                <Link
                  src={data.accountUrl}
                  style={commonStyles.link}
                >
                  View on {name}
                </Link>
                <Image
                  src={launchImage}
                  style={commonStyles.linkIcon}
                />
              </View>
            </View>
          </View>
        </View>

        <View style={[commonStyles.card, commonStyles.flex]}>
          <Text style={commonStyles.headerTitle}>Performance</Text>
          <View style={styles.performance}>
            <View style={styles.headerPerformance}>
              <Text style={styles.performanceText}>{data.percentage}%</Text>
              {data.chartImage && (
                <Image
                  src={data.chartImage}
                  style={styles.chartImage}
                />
              )}
            </View>
            <View style={styles.performanceValues}>
              {PERFORMANCE_POINTS.map((point) => {
                const value = data.overall?.[point.key] ?? 0

                return (
                  <View
                    key={point.key}
                    style={styles.performanceItem}
                  >
                    <Text style={styles.performanceLabel}>{point.label}</Text>
                    <Text
                      style={[
                        styles.performanceValue,
                        {
                          backgroundColor: STATUS_COLORS[point.key],
                        },
                      ]}
                    >
                      {value} point{value !== 1 ? 's' : ''}
                    </Text>
                  </View>
                )
              })}
            </View>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        {data.options.map((item, i) => (
          <View
            key={item.label}
            style={[styles.row, i === 0 ? styles.rowFirst : {}]}
          >
            <View>
              <Text style={styles.rowLabel}>{item.label}</Text>
              <Text style={styles.rowDescription}>{item.description}</Text>
            </View>
            <View style={styles.rowStatusWrapper}>
              <Text
                style={[
                  styles.rowStatus,
                  { backgroundColor: item.statusColor },
                ]}
              >
                {item.statusLabel}
              </Text>
              <Text style={styles.rowComment}>{item.comment}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  )
}
